import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { getEquipo, postequi, getSO, getMarca, getTipoequipo, getCritici } from '../../../redux/actions';
import {
    Container, TextField, Button, Grid, Paper, Typography, MenuItem, Select, FormControl, InputLabel, Modal, Box, IconButton, InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import CloseIcon from '@mui/icons-material/Close';
import {
    FaLaptop,
    FaBarcode,
    FaMicrochip,
    FaMemory,
    FaHdd,
    FaCalendarAlt,
    FaDollarSign,
    FaTag,
    FaCheckCircle,
    FaSearch,
    FaListUl,
    FaSave,
    FaEye
} from 'react-icons/fa';
import styles from './Crearequipos.module.css';

const FormularioEquipo = () => {
    const dispatch = useDispatch();

    const tipoequipo = useSelector((state) => state.tipoequipo || []);
    const sistemasO = useSelector((state) => state.sistemasOperativos || []);
    const equipos = useSelector((state) => state.equipos || []);
    const marca = useSelector((state) => state.marca || []);
    const criticidad = useSelector((state) => state.criticidad || []);

    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formValues, setFormValues] = useState({
        cod: '',
        modelo: '',
        serial: '',
        fechacompra: '',
        procesador: '',
        memoria: '',
        capacidadHD: '',
        tiporam: '',
        valor: '',
        tipoequipoId: '',
        SistemaOId: '',
        MarcaId: '',
        CriticId: ''
    });

    const tipoRAMOptions = ['DDR2', 'DDR3', 'DDR4', 'DDR5', '0'];

    useEffect(() => {
        dispatch(getSO());
        dispatch(getEquipo());
        dispatch(getMarca());
        dispatch(getTipoequipo());
        dispatch(getCritici());
    }, [dispatch]);

    const memoizedSistemasO = useMemo(() => sistemasO, [sistemasO]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setSearchTerm('');
        setOpen(false);
    };

    const formatCurrency = (value) => {
        if (!value) return '';
        const formattedValue = value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `$ ${formattedValue}`;
    };

    const parseCurrency = (value) => {
        return value.replace(/\./g, '').replace('$', '').trim();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'valor') {
            setFormValues({ ...formValues, [name]: formatCurrency(value) });
        } else {
            setFormValues({ ...formValues, [name]: value });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const requiredFields = ['cod', 'modelo', 'serial', 'fechacompra', 'procesador', 'memoria', 'capacidadHD', 'tiporam', 'valor', 'tipoequipoId', 'SistemaOId', 'MarcaId', 'CriticId'];
        for (const field of requiredFields) {
            if (!formValues[field]) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Campos incompletos',
                    text: 'Por favor, completa todos los campos antes de guardar.',
                    confirmButtonColor: '#3b82f6'
                });
                return;
            }
        }

        const equipoConEstado = {
            ...formValues,
            valor: parseCurrency(formValues.valor),
            estado: 'Bueno'
        };

        dispatch(postequi(equipoConEstado))
            .then(() => {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'Equipo registrado correctamente',
                    confirmButtonColor: '#10b981'
                });
                setFormValues({
                    cod: '', modelo: '', serial: '', fechacompra: '', procesador: '',
                    memoria: '', capacidadHD: '', tiporam: '', valor: '',
                    tipoequipoId: '', SistemaOId: '', MarcaId: '', CriticId: ''
                });
                dispatch(getEquipo());
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || 'Error al guardar el equipo.';
                Swal.fire({ icon: 'error', title: 'Error', text: errorMessage });
            });
    };

    const handleExportExcel = () => {
        if (filteredEquipos.length === 0) {
            Swal.fire({ icon: 'info', title: 'Sin datos', text: 'No hay equipos para exportar.' });
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(filteredEquipos);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Equipos");
        XLSX.writeFile(workbook, `Reporte_Equipos_${new Date().toLocaleDateString()}.xlsx`);

        Swal.fire({
            icon: 'success',
            title: '¡Descarga Iniciada!',
            text: 'El archivo Excel se está generando.',
            timer: 2000,
            showConfirmButton: false
        });
    };

    const filteredEquipos = equipos
        .filter((equipoObj) => {
            const cod = equipoObj.equipo.cod ? equipoObj.equipo.cod.toString().toLowerCase() : '';
            const modelo = equipoObj.equipo.modelo ? equipoObj.equipo.modelo.toLowerCase() : '';
            const serial = equipoObj.equipo.serial ? equipoObj.equipo.serial.toLowerCase() : '';
            const search = searchTerm.toLowerCase();
            return cod.includes(search) || modelo.includes(search) || serial.includes(search);
        })
        .map((equipoObj) => ({
            id: equipoObj.equipo.id,
            cod: equipoObj.equipo.cod,
            modelo: equipoObj.equipo.modelo,
            serial: equipoObj.equipo.serial,
            fechacompra: new Date(equipoObj.equipo.fechacompra).toLocaleDateString('es-ES'),
            procesador: equipoObj.equipo.procesador,
            memoria: equipoObj.equipo.memoria,
            capacidadHD: equipoObj.equipo.capacidadHD,
            tiporam: equipoObj.equipo.tiporam,
            valor: new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(equipoObj.equipo.valor),
            estado: equipoObj.equipo.estado,
            tipoequipoId: equipoObj.tipoequipo.descripcionte,
            SistemaOId: equipoObj.sistemao?.descripcionso || "",
            MarcaId: equipoObj.marca?.marca || "",
            CriticId: equipoObj.criticidad?.descripcion || "",
        }))
        .sort((a, b) => b.id - a.id);

    const columns = [
        { field: 'cod', headerName: 'Código', width: 120 },
        { field: 'modelo', headerName: 'Modelo', width: 130 },
        { field: 'serial', headerName: 'Serie', width: 150 },
        { field: 'tipoequipoId', headerName: 'Tipo', width: 130 },
        { field: 'MarcaId', headerName: 'Marca', width: 120 },
        { field: 'procesador', headerName: 'CPU', width: 130 },
        { field: 'memoria', headerName: 'RAM', width: 90 },
        { field: 'capacidadHD', headerName: 'Disco', width: 100 },
        { field: 'fechacompra', headerName: 'Compra', width: 110 },
        { field: 'valor', headerName: 'Valor', width: 120 },
        { field: 'CriticId', headerName: 'Criticidad', width: 120 },
    ];

    const getIcon = (key) => {
        switch (key) {
            case 'cod': return <FaBarcode />;
            case 'modelo': return <FaLaptop />;
            case 'serial': return <FaBarcode />;
            case 'procesador': return <FaMicrochip />;
            case 'memoria': return <FaMemory />;
            case 'capacidadHD': return <FaHdd />;
            case 'valor': return <FaDollarSign />;
            default: return <FaTag />;
        }
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h2 className={styles.title}>
                    <FaLaptop /> Registro de Equipos
                </h2>
                <p className={styles.subtitle}>Configura y añade nuevos activos tecnológicos al inventario.</p>
            </header>

            <Paper className={styles.formPaper}>
                <Typography className={styles.sectionTitle}>
                    <FaListUl /> Información Técnica y General
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {Object.entries(formValues).map(([key, value]) => {
                            if (['tipoequipoId', 'SistemaOId', 'MarcaId', 'CriticId', 'fechacompra', 'tiporam'].includes(key)) return null;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={key}>
                                    <TextField
                                        className={styles.inputField}
                                        name={key}
                                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                        variant="outlined"
                                        fullWidth
                                        value={value}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" style={{ color: '#94a3b8' }}>
                                                    {getIcon(key)}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            );
                        })}

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth className={styles.inputField}>
                                <InputLabel>Tipo de RAM</InputLabel>
                                <Select
                                    name="tiporam"
                                    value={formValues.tiporam}
                                    onChange={handleChange}
                                    label="Tipo de RAM"
                                    startAdornment={<InputAdornment position="start" style={{ color: '#94a3b8', marginRight: '8px' }}><FaMemory /></InputAdornment>}
                                >
                                    {tipoRAMOptions.map((ram) => (
                                        <MenuItem key={ram} value={ram}>{ram}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                className={styles.inputField}
                                name="fechacompra"
                                label="Fecha de Compra"
                                type="date"
                                fullWidth
                                value={formValues.fechacompra}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start" style={{ color: '#94a3b8' }}><FaCalendarAlt /></InputAdornment>,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth className={styles.inputField}>
                                <InputLabel>Marca</InputLabel>
                                <Select name="MarcaId" value={formValues.MarcaId} onChange={handleChange} label="Marca">
                                    {marca.map((tipo) => <MenuItem key={tipo.id} value={tipo.id}>{tipo.marca}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth className={styles.inputField}>
                                <InputLabel>Tipo de Equipo</InputLabel>
                                <Select name="tipoequipoId" value={formValues.tipoequipoId} onChange={handleChange} label="Tipo de Equipo">
                                    {tipoequipo.map((tipo) => <MenuItem key={tipo.id} value={tipo.id}>{tipo.descripcionte}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth className={styles.inputField}>
                                <InputLabel>Sistema Operativo</InputLabel>
                                <Select name="SistemaOId" value={formValues.SistemaOId} onChange={handleChange} label="Sistema Operativo">
                                    {memoizedSistemasO.map((so) => <MenuItem key={so.id} value={so.id}>{so.descripcionso}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth className={styles.inputField}>
                                <InputLabel>Criticidad</InputLabel>
                                <Select name="CriticId" value={formValues.CriticId} onChange={handleChange} label="Criticidad">
                                    {criticidad.map((tipo) => <MenuItem key={tipo.id} value={tipo.id}>{tipo.descripcion}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                            <Button type="submit" variant="contained" className={styles.submitBtn} startIcon={<FaSave />}>
                                Registrar Equipo
                            </Button>
                            <Button variant="outlined" className={styles.viewBtn} onClick={handleOpen} startIcon={<FaEye />}>
                                Ver Inventario
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            <Modal open={open} onClose={handleClose} closeAfterTransition>
                <Box className={styles.modalBox}>
                    <div className={styles.modalHeader}>
                        <Typography className={styles.modalTitle}>
                            <FaLaptop style={{ marginRight: '12px' }} /> Equipos Registrados
                        </Typography>
                        <IconButton onClick={handleClose} style={{ background: '#f1f5f9' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
                        <TextField
                            placeholder="Filtrar por código, modelo o serie..."
                            variant="outlined"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><FaSearch color="#3b82f6" /></InputAdornment>,
                                style: { borderRadius: '16px', background: '#f1f5f9', border: 'none' }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleExportExcel}
                            style={{
                                background: '#10b981',
                                borderRadius: '16px',
                                padding: '12px 24px',
                                textTransform: 'none',
                                fontWeight: '700',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                                display: 'flex',
                                gap: '8px',
                                minWidth: '180px'
                            }}
                        >
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1.2em" width="1.2em" xmlns="http://www.w3.org/2000/svg"><path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm60.1 106.5L224 336l60.1 93.5c5.1 8-.6 18.5-10.1 18.5h-26.1c-4.6 0-9-2.2-11.7-5.9L208 385.2l-28.2 56.9c-2.7 3.7-7.1 5.9-11.7 5.9h-26.1c-9.5 0-15.2-10.5-10.1-18.5l60.1-93.5-60.1-93.5c-5.1-8 .6-18.5 10.1-18.5h26.1c4.6 0 9 2.2 11.7 5.9L208 286.8l28.2-56.9c2.7-3.7 7.1-5.9 11.7-5.9h26.1c9.5 0 15.2 10.5 10.1 18.5zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 97.9c4.5 4.5 7 10.6 7 17z"></path></svg>
                            Exportar Excel
                        </Button>
                    </div>

                    <div className={styles.gridWrapper} style={{ height: 500 }}>
                        <DataGrid
                            rows={filteredEquipos}
                            columns={columns}
                            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 10 } },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            disableSelectionOnClick
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f1f5f9',
                                    color: '#475569',
                                    fontWeight: '800',
                                    borderBottom: '2px solid #e2e8f0'
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#eff6ff',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #f1f5f9',
                                    color: '#1e293b',
                                    fontWeight: '500'
                                }
                            }}
                        />
                    </div>
                </Box>
            </Modal>
        </div>
    );
};

export default FormularioEquipo;
